import { AbstractDestroyable, LogConfiguration, Logger, Revive } from '@deqo/foundation';
import { MantineProvider, createTheme } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/spotlight/styles.css';
import produce, { enableMapSet } from 'immer';
import { decorate, injectable } from 'inversify';
import React from 'react';
import ReactDOM from 'react-dom';
import slugify from 'slugify';
import './@css/index.css';
import { telemetry } from './@vendor/Posthog';
import { Host } from './Host';
import { App } from './app/App';
import { ServerDate } from './platform/ServerDate';
import { UserAgent } from './platform/UserAgent';
import * as serviceWorker from './serviceWorker';
import { Colors } from './ui';
import { Shadows } from './ui/Standards';

// c):)
ServerDate.sync();

// >:~@
enableMapSet();
Revive.produce = produce;

// :^)
decorate(injectable(), AbstractDestroyable);

// :-O
document.body.classList.add(
    'deqo',
    slugify(UserAgent.browser.name.toLowerCase()),
    slugify(`${UserAgent.browser.name.toLowerCase()}-${UserAgent.browser.version}`),
);

// ;-)
window.addEventListener('wheel', e => {
    if (e.ctrlKey) e.preventDefault();
}, { passive: false });

// >:)
telemetry.init();

// d:D
const urlParams = new URLSearchParams(window.location.search);
const useLogger = (key: string, enabled: boolean, color: string): LogConfiguration => ({
    [key]: [Boolean(urlParams.get(key) ?? enabled), color]
});
Logger.configure({
    ...useLogger('app.telemetry', Host.env === 'local', 'SlateBlue'),
    ...useLogger('app.api', false, 'Navy'),
    ...useLogger('app.hangout', false, 'Brown'),
    ...useLogger('app.metrics', false, 'DarkGreen'),
    ...useLogger('app.objects', false, 'Purple'),
    ...useLogger('app.events', Host.env === 'local', 'Chocolate'),
    ...useLogger('app.errors', true, 'Red'),
    ...useLogger('podopolis.model', false, 'MediumVioletRed'),
    ...useLogger('podopolis.connection', false, 'MediumVioletRed'),
    ...useLogger('podopolis.session', false, 'MediumVioletRed'),
    ...useLogger('podopolis.stats', false, 'DarkGreen'),
});

const inputStyles = {
    // Some styles added in mantine.css
    label: {
        color: Colors.text,
        fontWeight: 400,
        marginBottom: '.25rem'
    },
    input: {
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 5%)',
    },
};

const theme = createTheme({
    fontFamily: 'Rubik',
    primaryColor: 'primary',
    components: {
        Input: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        InputBase: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        TextInput: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        NumberInput: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        DateInput: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        PasswordInput: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        Select: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        PillsInput: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        MultiSelect: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        Textarea: {
            defaultProps: {
                size: 'md',
            },
            styles: inputStyles,
        },
        Button: {
            styles: (_, props) => ({
                root: {
                    boxShadow: props.variant !== 'subtle' ? Shadows.sm : '',
                    fontWeight: 450,
                    ...(props.variant === 'default' && {
                        color: Colors.text,
                    }),
                },
            }),
        },
        Checkbox: {
            defaultProps: {
                size: 'md',
            },
            styles: {
                label: {
                    color: Colors.text,
                }
            }
        },
        Switch: {
            defaultProps: {
                size: 'md',
                withThumbIndicator: false,
            },
        },
        Tabs: {
            styles: (_, props) => ({
                tab: {
                    borderBottomWidth: '.25rem',
                },
                tabLabel: {
                    fontSize: '1.125rem',
                    color: props.selected ? Colors.primary : Colors.text,
                }
            }),
        }
    },
    colors: {
        primary: [
            '#e8f0ff',
            '#e8f0ff',
            '#e8f0ff',
            '#d1dcff',
            '#a2b7fa',
            '#6f8ff5',
            '#3b65ef',
            '#2957ee',
            '#184cee',
            '#073ed4',
        ],
        positive: [
            '#e4fdfa',
            '#d7f4f0',
            '#b3e6df',
            '#8cd7cd',
            '#6ccbbe',
            '#57c3b4',
            '#40b8a8',
            '#37a99a',
            '#289689',
            '#078376'
        ],
        negative: [
            '#ffe8ec',
            '#ffe8ec',
            '#ffe8ec',
            '#ffd1d6',
            '#fba1aa',
            '#f66e7c',
            '#f13b4d',
            '#f0273b',
            '#ef162e',
            '#d50621',
        ],
        observe: [
            '#fff3e0',
            '#ffe6ca',
            '#ffcc99',
            '#ffb063',
            '#ff9f43',
            '#ff8918',
            '#ff8105',
            '#e46e00',
            '#cc6100',
            '#b15200'
        ],
        mystery: [
            '#f7e9ff',
            '#e8cfff',
            '#cd9bff',
            '#b974ff',
            '#9936fe',
            '#8b19fe',
            '#8309ff',
            '#7100e4',
            '#6400cc',
            '#5600b4'
        ],
    }
});

// const container = document.getElementById('app');
// createRoot(container).render(
//     <MantineProvider
//         defaultColorScheme="light"
//         withCssVariables
//         theme={theme}
//     >
//         <App />
//     </MantineProvider>
// );
const container = document.getElementById('app');
// createRoot(container).render(

// );

const AppWithMantine = () => (
    <MantineProvider
        defaultColorScheme="light"
        withCssVariables
        theme={theme}
    >
        <App />
    </MantineProvider>
);

ReactDOM.render(<AppWithMantine />, container); // This uses React 18 in legacy mode

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
// }
